import type { MatchAndClubCountryCode, PlayerSearchFilters, UserGender } from "@teamz/common";

import { ATPRankingBrackets, ITFRankingBrackets, matchAndClubCountryCodes, playerFFTranking, UTRRankingBrackets } from "@teamz/common";
import { SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { tDyn } from "@/lib/i18n-dynamic";
import { cn } from "@/lib/utils";

// Radix refuse une option de valeur vide : « voir tout » passe par cette
// sentinelle, retraduite en absence de filtre avant l'appel à l'API.
const ALL_VALUE = "all";

type FilterOption = { value: string; label: string };

function FilterSelect({ placeholder, options, value, onChange }: {
  placeholder: string;
  options: FilterOption[];
  value: string | null | undefined;
  onChange: (value: string | null) => void;
}) {
  return (
    <Select value={value ?? ALL_VALUE} onValueChange={next => onChange(next === ALL_VALUE ? null : next)}>
      <SelectTrigger className={cn("h-11 rounded-lg border-0 bg-neutral-700 text-white", !value && "text-neutral-200")}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value={ALL_VALUE} className="font-semibold">
            {placeholder}
          </SelectItem>
          <SelectSeparator />
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

// Barre de recherche des clubs freemium : le décompte porte sur toute la base,
// la liste affichée dessous n'en est qu'un extrait. À partir de md les six
// critères sont affichés en permanence ; en dessous la place manque, ils passent
// derrière un bouton pour que la page s'ouvre sur la liste et non sur un
// formulaire.
export function PlayerTeaserFilters({ filters, onFiltersChange }: {
  filters: PlayerSearchFilters;
  onFiltersChange: (filters: PlayerSearchFilters) => void;
}) {
  const { t } = useTranslation(["common"]);
  const { getCountryName } = useTranslationUtils();
  // Panneau ouvert d'entrée si la page est chargée avec des critères dans
  // l'URL (retour depuis une fiche joueur, lien partagé) : les critères actifs
  // doivent être visibles, pas cachés derrière le bouton.
  const [isOpen, setIsOpen] = useState(() => Object.values(filters).some(value => value !== null && value !== undefined));

  const activeFiltersCount = Object.values(filters).filter(value => value !== null && value !== undefined).length;
  const setFilter = (patch: PlayerSearchFilters) => onFiltersChange({ ...filters, ...patch });

  return (
    <div className="overflow-hidden md:rounded-xl">
      <div className="p-3 md:hidden">
        <Button
          type="button"
          variant="outline"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(open => !open)}
          className="w-full"
        >
          <SlidersHorizontal className="size-4" />
          {isOpen ? t("common:freemium.hide-filters") : t("common:freemium.show-filters")}
          {activeFiltersCount > 0 && (
            <span className="flex size-5 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white">
              {activeFiltersCount}
            </span>
          )}
        </Button>
      </div>

      <div className={cn(
        "flex-col gap-3 bg-neutral-600 p-3 duration-200 animate-in fade-in-0 slide-in-from-top-1 md:flex md:p-4",
        isOpen ? "flex" : "hidden",
      )}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <FilterSelect
            placeholder={t("common:availability-countries")}
            value={filters.country}
            onChange={country => setFilter({ country: country as MatchAndClubCountryCode | null })}
            options={matchAndClubCountryCodes.map(country => ({
              value: country,
              label: getCountryName(country.toUpperCase() as MatchAndClubCountryCode),
            }))}
          />

          <FilterSelect
            placeholder={t("common:gender")}
            value={filters.gender}
            onChange={gender => setFilter({ gender: gender as UserGender | null })}
            options={[
              { value: "male", label: t("common:man") },
              { value: "female", label: t("common:woman") },
            ]}
          />

          <FilterSelect
            placeholder={t("common:FFT-ranking")}
            value={filters.FFTRanking}
            onChange={ranking => setFilter({ FFTRanking: ranking as PlayerSearchFilters["FFTRanking"] })}
            options={playerFFTranking.map(ranking => ({ value: ranking, label: ranking }))}
          />

          <FilterSelect
            placeholder="UTR"
            value={filters.UTRBracket}
            onChange={bracket => setFilter({ UTRBracket: bracket as PlayerSearchFilters["UTRBracket"] })}
            options={UTRRankingBrackets.map(bracket => ({
              value: bracket,
              label: tDyn(t, `common:ranking-bracket.utr.${bracket}`) ?? bracket,
            }))}
          />

          <FilterSelect
            placeholder="ATP / WTA"
            value={filters.ATPBracket}
            onChange={bracket => setFilter({ ATPBracket: bracket as PlayerSearchFilters["ATPBracket"] })}
            options={ATPRankingBrackets.map(bracket => ({
              value: bracket,
              label: tDyn(t, `common:ranking-bracket.atp.${bracket}`) ?? bracket,
            }))}
          />

          <FilterSelect
            placeholder="ITF"
            value={filters.ITFBracket}
            onChange={bracket => setFilter({ ITFBracket: bracket as PlayerSearchFilters["ITFBracket"] })}
            options={ITFRankingBrackets.map(bracket => ({
              value: bracket,
              label: tDyn(t, `common:ranking-bracket.atp.${bracket}`) ?? bracket,
            }))}
          />
        </div>

        {activeFiltersCount > 0 && (
          <button
            type="button"
            onClick={() => onFiltersChange({})}
            className="self-start text-sm font-medium text-neutral-200 underline underline-offset-4 transition-colors hover:text-white"
          >
            {t("common:freemium.search-reset")}
          </button>
        )}
      </div>
    </div>
  );
}
